/**
 * @file index.tsx
 *
 * @fileoverview Renders a boolean type field on an
 * entity as a checkbox.
 */
import { EntityField } from '~/jobiqo/models/entity';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox } from '~/vcl/atoms/01-forms/02-checkbox';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useFieldRules } from '../../hooks/useFieldRules';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

const BooleanWidget = ({ entityField, fieldRules = {}, ...props }: Props) => {
  const { control, setValue, getValues } = useFormContext();
  const values = getValues();
  const { hidden, disabled, required } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      setValue(entityField.id, null);
    }
  }, [hidden, values, entityField]);

  if (hidden) {
    return null;
  }

  return (
    <Controller
      name={entityField.id}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <Checkbox
            {...field}
            checked={field.value}
            id={entityField.id}
            required={entityField.required || required}
            disabled={entityField.disabled || disabled}
            error={!!error}
            label={entityField.label}
            {...props}
          />
          {entityField.description && (
            <FormDescription content={entityField.description} />
          )}
          {!!error && <InlineError message={error.message} />}
        </>
      )}
    />
  );
};

export default BooleanWidget;
